import React from "react";
import { Typography } from "@mui/material";

const ReadingChartsGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        What is a Knitting Chart?
      </Typography>
      <Typography variant="body1">
        A knitting chart is a visual representation of a knitting pattern using symbols. Each square
        in the chart represents one stitch, and each row represents one row of knitting. Charts are
        read from bottom to top, just like you knit from the bottom up.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Chart Structure
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Rows</span>
          <span className="feature-item-description">
            Rows are numbered from bottom to top. Row 1 is at the bottom, and you work upward. In
            flat knitting, odd-numbered rows are right-side (RS) rows, and even-numbered rows are
            wrong-side (WS) rows.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Columns</span>
          <span className="feature-item-description">
            Columns represent stitches across the row. Column 1 is on the right side of the chart,
            matching how you read from right to left on right-side rows. This aligns with the
            standard reading direction when working flat knitting.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Symbols</span>
          <span className="feature-item-description">
            Each symbol represents a specific stitch or technique. Common symbols include ◊ for knit,
            ● for purl, ○ for yarn over, and many more. Refer to the stitch guide for complete
            symbol meanings.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Reading Direction
      </Typography>
      <Typography variant="body1">
        The direction you read a chart depends on whether you're working flat or in the round:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Flat Knitting (Back and Forth)</span>
          <span className="feature-item-description">
            Right-side rows (odd numbers): Read from right to left. Wrong-side rows (even numbers):
            Read from left to right. This matches how you actually knit—you turn your work after
            each row.
          </span>
        </li>
        <li>
          <span className="feature-item-title">In the Round</span>
          <span className="feature-item-description">
            All rows are read from right to left, since you're always working on the right side of
            the fabric. There are no wrong-side rows when knitting in the round.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Understanding Repeats
      </Typography>
      <Typography variant="body1">
        Many patterns include repeat sections, indicated by brackets or highlighted areas on the
        chart:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Pattern Repeats</span>
          <span className="feature-item-description">
            A section of the chart that's repeated multiple times across a row. For example, a
            4-stitch repeat means you work those 4 stitches, then repeat them across the row.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Row Repeats</span>
          <span className="feature-item-description">
            Some patterns repeat the same sequence of rows multiple times. This is often indicated by
            row numbers like 'Rows 1-4' with instructions to 'repeat rows 1-4' a certain number
            of times.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Chart Symbols
      </Typography>
      <Typography variant="body1">
        Each symbol in a chart has a specific meaning. While symbols can vary slightly between
        pattern sources, most follow Craft Yarn Council standards:
      </Typography>
      <ul className="feature-list">
        <li>Basic Stitches: ◊ = Knit (K), ● = Purl (P), ⧄ = No Stitch (empty space)</li>
        <li>
          Increases: ○ = Yarn Over (YO), ◐ = Make 1 Left (M1L), ◑ = Make 1 Right (M1R)
        </li>
        <li>
          Decreases: ◢ = Knit 2 Together (K2Tog), ◣ = Slip Slip Knit (SSK), ◥ = Purl 2 Together
          (P2Tog)
        </li>
        <li>
          Cables: ◄ = Cable Right, ► = Cable Left. Numbers indicate how many stitches are
          involved.
        </li>
      </ul>
      <Typography variant="body2" color="text.secondary">
        Always check the pattern's stitch key for symbol meanings, as some designers use custom
        symbols.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Tips for Reading Charts
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Use a Row Marker</span>
          <span className="feature-item-description">
            Place a sticky note or highlighter above the row you're currently working to keep your
            place.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Count Your Stitches</span>
          <span className="feature-item-description">
            Before starting each row, count the stitches in that row on the chart to make sure you
            have the correct number on your needles.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Check Your Gauge</span>
          <span className="feature-item-description">
            Make sure your gauge matches the pattern's gauge. If your gauge is different, your
            finished piece won't match the chart's proportions.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Read Ahead</span>
          <span className="feature-item-description">
            Look at the next few rows before you start knitting to understand how the pattern
            develops. This helps you catch mistakes early.
          </span>
        </li>
      </ul>
    </>
  );
};

export default ReadingChartsGuide;

